import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ProjectConnectionDetailsDto } from "@/api/types";
import { UsersPlusIcon } from "@/components/Icons/UsersPlusIcon";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { Heading } from "@/design-system/Heading";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions, OnboardingFlowRoot } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingTopBar } from "@/modules/onboarding-flow/components/OnboardingTopBar";
import { useTranslation } from "@/translations";

interface JoinProjectConnectionProps {
  invites: ProjectConnectionDetailsDto[];
  projectName: string;
  isJoining: boolean;
  onContinue: () => void;
}

export function JoinProjectConnection({
  invites,
  projectName,
  isJoining,
  onContinue,
}: JoinProjectConnectionProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const [selectedInvite, setSelectedInvite] = useState<ProjectConnectionDetailsDto>();

  const openInvite = (invite: ProjectConnectionDetailsDto) => {
    postHog.capture("new_connection_detail_clicked", { connection_id: invite.id });
    setSelectedInvite(invite);
  };

  return (
    <OnboardingFlowRoot>
      <OnboardingTopBar />
      <OnboardingFlowCard>
        <Stack gap="lg" align="center">
          <div className="flex items-center justify-center rounded-full bg-background-brand p-space-xl text-text-on-brand">
            <UsersPlusIcon size={48} />
          </div>
          <Stack gap="md" align="center">
            <Heading level={1} align="center" measure="compact">
              {t("page.project-connection.join.title")}
            </Heading>
            <Text as="p" measure="reading">
              {t("page.project-connection.join.description", { projectName })}
            </Text>
          </Stack>
        </Stack>

        <Stack gap="sm">
          <Text as="p">{t("page.project-connection.join.connected-to")}</Text>
          <Stack as="ul" gap="sm">
            {invites.map((invite) => (
              <li key={invite.id} className="flex items-baseline gap-space-sm">
                <span aria-hidden="true">•</span>
                <Button
                  variant="ghostPrimary"
                  underline
                  aria-label={t("page.project-connection.join.invite.accessibility-label", {
                    connectionName: invite.name,
                  })}
                  onClick={() => openInvite(invite)}
                >
                  {invite.name}
                </Button>
              </li>
            ))}
          </Stack>
          <Text as="p">{t("page.project-connection.join.continue")}</Text>
        </Stack>

        <OnboardingFlowActions>
          <Button fullWidth isLoading={isJoining} onClick={onContinue}>
            {t("common.action.continue")}
          </Button>
        </OnboardingFlowActions>
      </OnboardingFlowCard>

      <ProjectConnectionDetailsModal invite={selectedInvite} onClose={() => setSelectedInvite(undefined)} />
    </OnboardingFlowRoot>
  );
}

function ProjectConnectionDetailsModal({
  invite,
  onClose,
}: {
  invite: ProjectConnectionDetailsDto | undefined;
  onClose: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const resolveImage = useImageResolver();
  const backgroundImage = invite?.backgroundImage;

  return (
    <Modal.Root title={invite?.name} isOpened={invite !== undefined} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Modal.Body withPadding={backgroundImage === undefined}>
        <Stack gap="md">
          {backgroundImage && (
            <img className="aspect-video w-full object-cover" src={resolveImage(backgroundImage, "intrinsic")} alt="" />
          )}
          <div className={backgroundImage ? "px-space-md" : undefined}>
            <Text as="p" overflow="preserve">
              {invite?.content}
            </Text>
          </div>
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button variant="filled">{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
